<template>
  <supplier-suggestion-sheet
    mode="product"
    :opened="state.opened"
    :product="state.product"
    :item="null"
    :suppliers="suppliers"
    :reward-cents="rewardCents"
    :loading="suppliersLoading"
    :failed="suppliersFailed"
    @update:opened="setOpened"
    @submitted="onSubmitted"
    @stale="onStale"
  ></supplier-suggestion-sheet>
</template>

<script setup>
/**
 * ProductSuggestionHost — the ONE product-suggestion sheet on a page.
 *
 * Mount it once, at PAGE level, on any page that renders post cards or a
 * product. It must sit OUTSIDE VirtualPostList's rows: those are positioned
 * with `transform`, which makes them the containing block for `position: fixed`
 * descendants, so a sheet rendered inside one is trapped in that row's box. The
 * door inside a card asks through `useProductSuggestion()` instead of hosting
 * its own — see that composable for the whole reasoning.
 *
 * ── WHAT IT DOES WITH THE 200 ───────────────────────────────────────────────
 * The response is the PRODUCT resource, already carrying `mySuggestion`, so
 * nothing re-fetches to find out what was recorded. Two saves follow:
 *
 *  1. The product itself, so the product page's receipt paints.
 *  2. The POST she tapped from, patched with the same payload — because a post
 *     carries the UNION of its products' tips, newest wins, and hers is by
 *     definition the newest. Without this the card behind the sheet would keep
 *     offering the door until the next feed read.
 *
 * The post patch writes `{ id, mySuggestion }` and nothing else: Post has no
 * omitted-key hook, so pinia-orm's merge leaves the rest of the row alone.
 */
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SupplierSuggestionSheet from '@/components/orders/SupplierSuggestionSheet.vue';
import { useProductSuggestion } from '@/composables/commerce/useProductSuggestion.js';
import { useSupplierList } from '@/composables/commerce/useSupplierList.js';
import Product from '@/models/commerce/Product.js';
import { patchHeldPost } from '@/models/posts/postFeedModels.js';

defineOptions({ name: 'ProductSuggestionHost' });

const { t } = useI18n({ useScope: 'global' });
const { state, close, setOpened } = useProductSuggestion();
// Fetched when the sheet opens, never cached across opens — the roster she picks
// from and the id the backend validates have to be read at the same instant.
// See useSupplierList for why the backend's own cache was removed.
const {
  suppliers,
  rewardCents,
  loading: suppliersLoading,
  failed: suppliersFailed,
  load: loadSuppliers,
} = useSupplierList();

// The door sets `opened` through the composable, so this is what turns "she
// tapped" into "fetch the roster". The sheet renders its typed field
// immediately and the picker appears if and when the roster lands; a failure is
// "no list", never an error.
watch(() => state.opened, (isOpen) => {
  if (isOpen) loadSuppliers(state.product?.id);
});

function toast(text) {
  f7.toast.create({ text, closeTimeout: 2500, horizontalPosition: 'center' }).open();
}

function onSubmitted(product) {
  const suggestion = product?.mySuggestion ?? null;

  useRepo(Product).save({ id: String(product.id), mySuggestion: suggestion });

  // Patch the card she tapped from. EVERY post table is a candidate, because
  // the bookmarks tab and the تدبيسة page are the same PostCard reading a
  // different model (`Bookmark` / `TadbeesaPost` extend Post) and a post in one
  // table is invisible to the others — she can perfectly well name a shop from
  // a saved post.
  //
  // ONLY WHERE THE ROW ALREADY EXISTS, though. pinia-orm's `save` is an upsert,
  // so writing blind would INSERT a bookmark for a post she never saved, and
  // that phantom would render in the bookmarks tab as a card with no channel,
  // no media and no price. patchHeldPost owns that rule.
  if (state.postId != null) {
    patchHeldPost({ id: state.postId, mySuggestion: suggestion });
  }

  toast(t('item.suggestSupplier.productSuccess'));
  close();
}

/**
 * A 403 on this door is not "the line moved on" — there is no line. It is "this
 * door is not yours to open": no membership, one still under review or
 * rejected, or a Suppliers Club member. Exactly the rule behind the supplier
 * roster she picks from, so it should be unreachable in practice; when it does
 * happen there is nothing for her to fix in the form, so the sheet has already
 * left and this only says what happened.
 */
function onStale({ message }) {
  toast(message);
  close();
}
</script>
